import { pkceUserProfile } from "@/types/auth/pkceUserProfile";
import isTokenValid from "./isTokenValid";
import parseJWT from "./parseJWT";

/**
 * Cross-tab auth handoff. The CFP SDK caches tokens in sessionStorage, which is per tab, so a
 * tab opened from the app would otherwise start with no token and redo silent auth. Instead it
 * asks the open tabs for their in-memory session over a same-origin BroadcastChannel. Tokens are
 * only ever passed in memory — never written to localStorage.
 */

const AUTH_CHANNEL_NAME = "appf-auth";
const PEER_SESSION_TIMEOUT_MS = 750;

// Key prefix identity-web-sdk (inside the CFP runtime) uses for its token cache entries.
const SDK_TOKEN_CACHE_PREFIX = "@@authspasdk@@";

export type AuthSession = {
  accessToken: string;
  userProfile: pkceUserProfile;
};

export type AuthChannelMessage =
  | { type: "session-request"; requestId: string }
  | { type: "session-response"; requestId: string; session: AuthSession }
  | { type: "session-updated"; session: AuthSession }
  | { type: "logout" };

export const openAuthChannel = (): BroadcastChannel | null => {
  if (typeof window === "undefined" || typeof window.BroadcastChannel === "undefined") return null;
  return new BroadcastChannel(AUTH_CHANNEL_NAME);
};

export const isUsableSession = (session: AuthSession | null | undefined): session is AuthSession =>
  !!session?.userProfile?.userId && isTokenValid(session.accessToken);

/** Resolves with a valid session from another open tab, or null if none answers in time. */
export const requestSessionFromPeers = (timeoutMs: number = PEER_SESSION_TIMEOUT_MS): Promise<AuthSession | null> => {
  const channel = openAuthChannel();
  if (!channel) return Promise.resolve(null);

  const requestId = `${Date.now()}-${Math.random().toString(36).slice(2)}`;

  return new Promise((resolve) => {
    const finish = (session: AuthSession | null) => {
      clearTimeout(timer);
      channel.close();
      resolve(session);
    };
    const timer = setTimeout(() => finish(null), timeoutMs);

    channel.onmessage = (event: MessageEvent<AuthChannelMessage>) => {
      const message = event.data;
      if (message?.type === "session-response" && message.requestId === requestId && isUsableSession(message.session)) {
        finish(message.session);
      }
    };
    channel.postMessage({ type: "session-request", requestId } satisfies AuthChannelMessage);
  });
};

/** True when this tab already holds an SDK token cache (e.g. a reload), so getAccessToken is cheap. */
export const hasSdkTokenCache = (): boolean => {
  try {
    return Object.keys(sessionStorage).some((key) => key.startsWith(SDK_TOKEN_CACHE_PREFIX));
  } catch {
    return false;
  }
};

/** True while the URL carries an OAuth authorization-code callback the SDK still has to exchange. */
export const isAuthRedirectCallback = (): boolean => {
  const params = new URLSearchParams(window.location.search);
  return params.has("code") && params.has("state");
};

/** Seconds until the JWT expires; 0 when it is missing, malformed, or already expired. */
export const getTokenSecondsLeft = (token: string): number => {
  const decoded = parseJWT(token) as { exp?: number } | false;
  if (!decoded || !decoded.exp) return 0;
  return Math.max(0, Math.floor(decoded.exp - Date.now() / 1000));
};
